/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import "./CodeActionMenuPlugin.css";

import { $isCodeNode, CodeNode, getLanguageFriendlyName } from "@lexical/code";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getNearestNodeFromDOMNode, $getSelection, $setSelection } from "lexical";
import { debounce } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

const CODE_PADDING = 8;

function CodeActionMenuContainer() {
  const [editor] = useLexicalComposerContext();

  const [lang, setLang] = useState("");
  const [isShown, setShown] = useState(false);
  const [shouldListenMouseMove, setShouldListenMouseMove] = useState(false);
  const [isCopyCompleted, setCopyCompleted] = useState(false);
  const [position, setPosition] = useState({
    right: "0",
    top: "0",
  });
  const codeSetRef = useRef(new Set<string>());
  const codeDOMNodeRef = useRef<Element | null>(null);

  const removeSuccessIcon = useDebounce(
    () => {
      setCopyCompleted(false);
    },
    1000,
    1000
  );

  const debouncedOnMouseMove = useDebounce(
    (event: MouseEvent) => {
      const { codeDOMNode, isOutside } = getMouseInfo(event);

      if (isOutside) {
        setShown(false);
        return;
      }

      if (!codeDOMNode) {
        return;
      }

      codeDOMNodeRef.current = codeDOMNode;

      let codeNode = null;
      let _lang = "";

      editor.update(() => {
        const maybeCodeNode = $getNearestNodeFromDOMNode(codeDOMNode);

        if ($isCodeNode(maybeCodeNode)) {
          codeNode = maybeCodeNode;
          _lang = codeNode.getLanguage() || "";
        }
      });

      if (codeNode) {
        const { y, right } = codeDOMNode.getBoundingClientRect();
        setLang(_lang);
        setShown(true);
        setPosition({
          right: `${window.innerWidth - right + CODE_PADDING - window.pageXOffset}px`,
          top: `${y + window.pageYOffset}px`,
        });
      }
    },
    50,
    1000
  );

  useEffect(() => {
    if (!shouldListenMouseMove) {
      return;
    }

    document.addEventListener("mousemove", debouncedOnMouseMove);

    return () => {
      setShown(false);
      debouncedOnMouseMove.cancel();
      document.removeEventListener("mousemove", debouncedOnMouseMove);
    };
  }, [shouldListenMouseMove, debouncedOnMouseMove]);

  editor.registerMutationListener(CodeNode, mutations => {
    editor.update(() => {
      for (const [key, type] of mutations) {
        switch (type) {
          case "created":
            codeSetRef.current.add(key);
            setShouldListenMouseMove(codeSetRef.current.size > 0);
            break;

          case "destroyed":
            codeSetRef.current.delete(key);
            setShouldListenMouseMove(codeSetRef.current.size > 0);
            break;

          default:
            break;
        }
      }
    });
  });

  async function handleClick() {
    const codeDOMNode = codeDOMNodeRef.current;
    if (!codeDOMNode) {
      return;
    }

    editor.update(async () => {
      const codeNode = $getNearestNodeFromDOMNode(codeDOMNode);

      if ($isCodeNode(codeNode)) {
        const text = codeNode.getTextContent();
        const selection = $getSelection();
        $setSelection(selection);

        try {
          await navigator.clipboard.writeText(text);
          setCopyCompleted(true);
          removeSuccessIcon();
        } catch (err) {
          console.error("Failed to copy: ", err);
        }
      }
    });
  }

  const codeFriendlyName = getLanguageFriendlyName(lang);

  return (
    <>
      {isShown ? (
        <div className="code-action-menu-container" style={{ ...position }}>
          <div className="code-highlight-language">{codeFriendlyName}</div>
          <button className="menu-item" onClick={handleClick} aria-label="copy">
            {isCopyCompleted ? <i className="format success" /> : <i className="format copy" />}
          </button>
        </div>
      ) : null}
    </>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyFunction = (...args: any[]) => any;

function useDebounce<T extends AnyFunction>(fn: T, ms: number, maxWait: number) {
  const funcRef = useRef<T | null>(null);
  funcRef.current = fn;

  return useMemo(
    () =>
      debounce(
        (...args: Parameters<T>) => {
          if (funcRef.current) {
            funcRef.current(...args);
          }
        },
        ms,
        { maxWait }
      ),
    [ms, maxWait]
  );
}

function getMouseInfo(event: MouseEvent): { codeDOMNode: Element | null; isOutside: boolean } {
  const target = event.target;

  if (target && target instanceof HTMLElement) {
    const codeDOMNode = target.closest("code.PlaygroundEditorTheme__code");
    const isOutside = !(codeDOMNode || target.closest("div.code-action-menu-container"));

    return { codeDOMNode, isOutside };
  } else {
    return { codeDOMNode: null, isOutside: true };
  }
}

export default function CodeActionMenuPlugin() {
  return createPortal(<CodeActionMenuContainer />, document.body);
}
